// portalService.ts — API client for /api/v1/portal/* endpoints
import apiClient from '../../../services/apiClient';
import type { DoctorSummary, TimeSlot, BookingRequest, BookingResponse } from '../../../types/portal';

export interface PortalProfile {
    patient: {
        id: number;
        identification: string | null;
        first_name: string;
        last_name: string;
        birth_date: string | null;
        address: string | null;
        email: string | null;
        phone: string | null;
        mobile: string | null;
        gender: boolean | null;
        status: string | null;
        is_oncology: boolean;
    };
    user: {
        id: number;
        email: string;
        name: string;
    } | null;
}

export interface ProfileUpdateRequest {
    address?: string;
    phone?: string;
    mobile?: string;
    email?: string;
}

export interface PortalAppointment {
    id: number;
    patient_id: number;
    scheduled_date: string;
    scheduled_time: string;
    reason: string | null;
    doctor: {
        id: number;
        first_name: string;
        last_name: string;
    } | null;
    status: {
        id: number;
        name: string;
    } | null;
    medical_consultation: {
        id: number;
        consultation_modality: string;
        video_call_link: string | null;
    } | null;
}

export interface PortalHistory {
    consultations: {
        id: number;
        consultation_date: string | null;
        doctor: { id: number; first_name: string; last_name: string } | null;
        chief_complaint: string | null;
        present_illness: string | null;
        general_recommendations: string | null;
    }[];
    diagnoses: {
        id: number;
        consultation_id: number;
        diagnosis: { id: number; code: string; name: string } | null;
        type: string | null;
        observations: string | null;
    }[];
    prescriptions: {
        id: number;
        consultation_id: number;
        medication_name: string;
        dosage: string | null;
        frequency: string | null;
        duration: string | null;
        instructions: string | null;
    }[];
}

interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

export const portalService = {
    async getProfile(): Promise<PortalProfile> {
        const response = await apiClient.get<ApiResponse<PortalProfile>>('/portal/me');
        return response.data.data;
    },

    async updateProfile(data: ProfileUpdateRequest): Promise<PortalProfile> {
        const response = await apiClient.put<ApiResponse<PortalProfile>>('/portal/me', data);
        return response.data.data;
    },

    async getAppointments(params?: {
        status?: string;
        status_id?: number;
        date?: string;
        doctor_id?: number;
    }): Promise<{ data: PortalAppointment[]; total: number }> {
        const response = await apiClient.get<
            ApiResponse<{ data: PortalAppointment[]; total: number }>
        >('/portal/mis-citas', { params });
        return response.data.data;
    },

    async cancelAppointment(id: number): Promise<void> {
        await apiClient.delete(`/portal/citas/${id}`);
    },

    async getHistory(): Promise<PortalHistory> {
        const response = await apiClient.get<ApiResponse<PortalHistory>>('/portal/mi-historial');
        return response.data.data;
    },
};

export default portalService;

// Booking API — used by BookAppointmentPage

export const bookingService = {
    async getDoctors(): Promise<DoctorSummary[]> {
        const response = await apiClient.get<{ success: boolean; data: DoctorSummary[] }>('/doctors');
        return response.data.data;
    },

    async getSlots(doctorId: number, date: string): Promise<TimeSlot[]> {
        const response = await apiClient.get<{ success: boolean; data: TimeSlot[] }>(
            `/doctors/${doctorId}/slots`,
            { params: { date } },
        );
        return response.data.data;
    },

    async bookAppointment(request: BookingRequest): Promise<BookingResponse> {
        const response = await apiClient.post<ApiResponse<BookingResponse>>(
            '/portal/agendar-cita',
            request,
        );
        return response.data.data;
    },
};
